import {Box2Like, Vector2Like} from "@cm/math"
import {HalImage, HalImageChannelLayout, TypedArrayImage, getNumChannels} from "@cm/hal"

export const createTypedArrayImage = (args: {width: number; height: number; channelLayout: HalImageChannelLayout}): TypedArrayImage => {
    return {
        width: args.width,
        height: args.height,
        channelLayout: args.channelLayout,
        dataType: "float32",
        data: new Float32Array(args.width * args.height * getNumChannels(args.channelLayout)),
    }
}

export const copyTypedArrayImage = (args: {source: TypedArrayImage; sourceRegion?: Box2Like; target: TypedArrayImage; targetOffset?: Vector2Like}): void => {
    const sourceRegion = args.sourceRegion ?? {
        x: 0,
        y: 0,
        width: args.source.width,
        height: args.source.height,
    }
    if (
        sourceRegion.x < 0 ||
        sourceRegion.y < 0 ||
        sourceRegion.x + sourceRegion.width > args.source.width ||
        sourceRegion.y + sourceRegion.height > args.source.height
    ) {
        throw new Error("Source region out of bounds")
    }
    const targetOffset = args.targetOffset ?? {
        x: 0,
        y: 0,
    }
    if (
        targetOffset.x < 0 ||
        targetOffset.y < 0 ||
        targetOffset.x + sourceRegion.width > args.target.width ||
        targetOffset.y + sourceRegion.height > args.target.height
    ) {
        throw new Error("Target offset out of bounds")
    }
    if (args.source.channelLayout !== args.target.channelLayout) {
        throw new Error("Channel layout mismatch")
    }
    if (args.source.dataType !== args.target.dataType) {
        throw new Error("Data type mismatch")
    }
    const numChannels = getNumChannels(args.source.channelLayout)
    // const bytesPerChannel = getBytesPerChannel(args.source.dataType)
    // const bytesPerPixel = numChannels * bytesPerChannel
    const sourceData = args.source.data
    const targetData = args.target.data
    for (let row = 0; row < sourceRegion.height; row++) {
        const srcStart = ((sourceRegion.y + row) * args.source.width + sourceRegion.x) * numChannels
        const srcEnd = srcStart + sourceRegion.width * numChannels
        const dstStart = ((targetOffset.y + row) * args.target.width + targetOffset.x) * numChannels
        targetData.set(sourceData.subarray(srcStart, srcEnd), dstStart)
    }
}

export const createTypedArrayImageFromHalImage = (args: {source: HalImage; sourceRegion?: Box2Like; sourceMipLevel?: number}): TypedArrayImage => {
    const sourceRegion = args.sourceRegion ?? {
        x: 0,
        y: 0,
        width: args.source.descriptor.width,
        height: args.source.descriptor.height,
    }
    const sourceMipLevel = args.sourceMipLevel ?? 0
    return {
        width: sourceRegion.width,
        height: sourceRegion.height,
        channelLayout: args.source.descriptor.channelLayout,
        dataType: "float32",
        data: args.source.readImageDataFloat({region: sourceRegion, mipLevel: sourceMipLevel}),
    }
}

export const halImageToTypedArrayImage = (args: {
    source: HalImage
    sourceRegion?: Box2Like
    sourceMipLevel?: number
    target: TypedArrayImage
    targetOffset?: Vector2Like
}): void => {
    const sourceData = createTypedArrayImageFromHalImage({
        source: args.source,
        sourceRegion: args.sourceRegion,
        sourceMipLevel: args.sourceMipLevel,
    })
    copyTypedArrayImage({
        source: sourceData,
        target: args.target,
        targetOffset: args.targetOffset,
    })
}

export const halImageFromTypedArrayImage = async (args: {
    source: TypedArrayImage
    sourceRegion?: Box2Like
    target: HalImage
    targetOffset?: Vector2Like
    targetMipLevel?: number
}): Promise<void> => {
    const sourceRegion = args.sourceRegion ?? {
        x: 0,
        y: 0,
        width: args.source.width,
        height: args.source.height,
    }
    const targetOffset = args.targetOffset ?? {
        x: 0,
        y: 0,
    }
    const targetMipLevel = args.targetMipLevel ?? 0
    await args.target.writeImageData({
        sourceData: args.source,
        sourceRegion,
        targetOffset,
        targetMipLevel,
    })
}
